import React from 'react';
import { I18n } from '@aws-amplify/core';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';

import { Charts } from './Charts';
import { Parameters } from './Parameters';
import { ErrorMessage } from './ErrorMessage';
import Spinner from '@/components/Layout/Spinner';
import { getScore, getScoreParts, getNotesPerSecond } from '../services';
import { ReportTemplate } from '@/components/views/Report/ReportTemplate';
import { useApplicationContext } from '@/context/v1/Application/context';


export function Result({
	file,
	report,
	sectors,
	onDownloadPDF,
	isLoadingPDF,
	onChangeComment
}) {
	const history = useHistory();
	const { file_id } = useParams();

	const { isSandbox, sendReport, existsReport } = useApplicationContext();

	const organization = useSelector((state) => state.organization.organization);
	const company = useSelector((state) => state.organization.company);

	const parameters = {
		organization_id: organization.id,
		company_id: company.id,
		file_id: file_id
	};

	const exists = existsReport({
		file_id: file.data.id,
		company_id: company?.id,
		organization_id: organization?.id,
		report_type: 'REBA'
	});

	function handleSendReport() {
		const request = {
			report_type: 'REBA',
			id: report.data.id || '',
			company_id: company?.id,
			organization_id: organization?.id
		};

		const currentPath = history.location.pathname;
		const cleanPath = currentPath.match(/(\/reporting\/reba)/)?.[1];

		if (cleanPath && currentPath !== cleanPath) {
			history.replace(cleanPath);
		}

		sendReport(request, '/reporting');
	}

	const score = useQuery(['reba-score', parameters], () => getScore(parameters));
	const scorePerSecond = useQuery(['reba-note-per-second', parameters], () =>
		getNotesPerSecond(parameters)
	);
	const scoreParts = useQuery(['reba-score-parts', parameters], () =>
		getScoreParts(parameters)
	);

	const isError = score.isError || scoreParts.isError || scorePerSecond.isError;
	const isLoading = score.isLoading || scoreParts.isLoading || scorePerSecond.isLoading;

	const selectedSector = sectors.data.find((sector) => sector.id === file.data.sector_id);

	const onRetry = () => window.location.reload();

	const sections = [
		{ title: I18n.get('Parameters'), component: <Parameters report={report.data} /> },
		{
			title: I18n.get('Analysis results'),
			component: (
				<Charts
					score={score.data}
					parts={scoreParts.data}
					scorePerSecond={scorePerSecond.data}
				/>
			)
		}
	];

	if (isLoading) {
		return <Spinner />;
	}

	if (isError) {
		return <ErrorMessage onRetry={onRetry} />;
	}

	return (
		<ReportTemplate
			title={I18n.get('REBA')}
			sections={sections}
			reportData={report.data}
			fileData={file.data}
			sector={selectedSector}
			isLoading={isLoading}
			isLoadingPDF={isLoadingPDF}
			onDownloadPDF={onDownloadPDF}
			onChangeComment={onChangeComment}
			handleSendReport={handleSendReport}
			exists={exists}
		/>
	);
}
